// useRoute.ts — flat hash-based router shared by NavBar and Root.
//
// All site-wide routes live here. Encodes/decodes the URL hash and notifies
// subscribers when it changes. Keeps Portal's old "showAdmin/showDatabase"
// state machine and DatabaseModule's "activeView" out of the picture for
// top-level nav — those become drill-down concerns only.

import { useEffect, useState, useCallback } from "react";

export type Route =
  | "launchpad"
  | "app"
  | "packaging"
  | "compare"
  | "goldenratio"
  | "database"
  | "ingredients"
  | "formulas"
  | "account"
  | "admin";

const ALL_ROUTES: Route[] = [
  "launchpad",
  "app",
  "packaging",
  "compare",
  "goldenratio",
  "database",
  "ingredients",
  "formulas",
  "account",
  "admin",
];

export function parseHash(): Route {
  const raw = (window.location.hash || "").replace(/^#/, "").split("/")[0];
  if ((ALL_ROUTES as string[]).includes(raw)) return raw as Route;
  return "launchpad";
}

export function setRoute(r: Route, opts?: { replace?: boolean }) {
  const next = r === "launchpad" ? "" : `#${r}`;
  if (opts?.replace) {
    window.history.replaceState({}, "", window.location.pathname + window.location.search + next);
  } else {
    window.location.hash = next;
  }
  // hashchange does not fire when the hash is identical — dispatch manually so
  // route subscribers still re-read.
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export function useRoute(): [Route, (r: Route) => void] {
  const [route, setRouteState] = useState<Route>(() => parseHash());

  useEffect(() => {
    const onHash = () => setRouteState(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = useCallback((r: Route) => setRoute(r), []);
  return [route, navigate];
}
